import posthog from 'posthog-js'

/**
 * PostHog Analytics Service
 *
 * Provides analytics tracking for user behavior and product insights.
 * Initializes PostHog client and exposes tracking functions.
 *
 * Uses a reverse proxy (/ingest) to avoid ad blockers.
 *
 * Environment Variables Required:
 * - REACT_APP_POSTHOG_KEY: PostHog project API key
 * - REACT_APP_POSTHOG_HOST: PostHog host (used as feature flag to enable tracking)
 */

class PostHogService {
  constructor() {
    this.initialized = false
    this.isEnabled = false
  }

  /**
   * Initialize PostHog client with configuration
   * Only initializes if credentials are provided
   * Safe to call multiple times - will only init once
   */
  init() {
    // Prevent double initialization
    if (this.initialized) {
      return
    }

    // Skip analytics entirely during build-time prerendering (scripts/prerender.js)
    if (typeof window !== 'undefined' && window.__PRERENDER__) {
      this.initialized = true
      this.isEnabled = false
      return
    }

    const apiKey = process.env.REACT_APP_POSTHOG_KEY
    const host = process.env.REACT_APP_POSTHOG_HOST

    // Only initialize if credentials are provided
    if (!apiKey || !host) {
      // eslint-disable-next-line no-console
      console.log('PostHog: Not configured (missing credentials)')
      this.initialized = true
      this.isEnabled = false
      return
    }

    try {
      // Use reverse proxy to avoid ad blockers, fallback to direct host
      const apiHost = window.location.origin + '/ingest'

      posthog.init(apiKey, {
        api_host: apiHost,
        // Use latest defaults snapshot for modern SPA support
        // Includes: capture_pageview: 'history_change', rageclick content_ignorelist,
        // session_recording strictMinimumDuration, external_scripts_inject_target: 'head'
        defaults: '2026-01-30',
        // Session recording with canvas support for memory visualization charts
        session_recording: {
          recordCanvas: true,
          canvasFps: 4,
          canvasQuality: 'medium'
        },
        // Respect user privacy
        respect_dnt: false,
        // Persistence
        persistence: 'localStorage+cookie',
        // Cross-subdomain cookie for better tracking
        cross_subdomain_cookie: true,
        // Secure cookies in production
        secure_cookie: window.location.protocol === 'https:',
        // Debug logging in development
        loaded: (ph) => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('PostHog: Initialized successfully')
          }
        }
      })

      this.initialized = true
      this.isEnabled = true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Failed to initialize', error)
      this.initialized = true
      this.isEnabled = false
    }
  }

  /**
   * Identify a user with properties
   * @param {string} userId - Unique user identifier
   * @param {object} properties - User properties (email, name, etc.)
   */
  identify(userId, properties = {}) {
    if (!this.isEnabled) return

    try {
      posthog.identify(userId, properties)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Error identifying user', error)
    }
  }

  /**
   * Track a page view
   * @param {string} pageName - Name of the page
   * @param {object} properties - Additional properties
   */
  trackPageView(pageName, properties = {}) {
    if (!this.isEnabled) return

    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        page_name: pageName,
        ...properties
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Error tracking page view', error)
    }
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {object} properties - Event properties
   */
  trackEvent(eventName, properties = {}) {
    if (!this.isEnabled) return

    try {
      posthog.capture(eventName, properties)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Error tracking event', error)
    }
  }

  /**
   * Reset user identity (call on logout)
   * Clears all user data and generates a new anonymous ID
   */
  reset() {
    if (!this.isEnabled) return

    try {
      posthog.reset()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Error resetting', error)
    }
  }

  /**
   * Set user properties (person properties - stored globally on user profile)
   * @param {object} properties - Properties to set
   */
  setPersonProperties(properties = {}) {
    if (!this.isEnabled) return

    try {
      posthog.setPersonProperties(properties)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Error setting person properties', error)
    }
  }

  /**
   * Register super properties (event properties sent with every event)
   * These are session-scoped and not retroactively applied like person properties
   * @param {object} properties - Properties to register
   */
  registerSuperProperties(properties = {}) {
    if (!this.isEnabled) return

    try {
      posthog.register(properties)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Error registering super properties', error)
    }
  }

  /**
   * Unregister super properties
   * @param {string|string[]} propertyNames - Property name(s) to unregister
   */
  unregisterSuperProperties(propertyNames) {
    if (!this.isEnabled) return

    try {
      if (Array.isArray(propertyNames)) {
        propertyNames.forEach(name => posthog.unregister(name))
      } else {
        posthog.unregister(propertyNames)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PostHog: Error unregistering super properties', error)
    }
  }

  /**
   * Check if PostHog is enabled
   * @returns {boolean}
   */
  get enabled() {
    return this.isEnabled
  }
}

// Export singleton instance
const postHogService = new PostHogService()
export default postHogService
